import type {
  TesterChannel,
  TesterRunResult,
  TesterService,
} from "@xop/dashboard-shared";
import {
  DEFAULT_DIALOUT_CALLER_ID,
  TESTER_CHANNELS,
  TESTER_CHANNEL_LABELS,
  TESTER_LIVE_SERVICES,
  TESTER_SERVICES,
  TESTER_SERVICE_LABELS,
} from "@xop/dashboard-shared";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useCan } from "../../auth/permissions.js";
import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { EmptyState } from "../../components/ui/empty-state.js";
import { PageHeader } from "../../components/ui/page-header.js";
import { ApiError } from "../../lib/api.js";
import { SignalEmitter } from "./decor.js";
import { testerApi } from "./client.js";

const QK = ["tester", "targets"] as const;

export function TesterRunPage(): JSX.Element {
  const canRun = useCan("tester:run");
  const targetsQuery = useQuery({ queryKey: QK, queryFn: testerApi.listTargets });
  const targets = useMemo(
    () => targetsQuery.data?.targets ?? [],
    [targetsQuery.data],
  );

  const [targetId, setTargetId] = useState<number | "">("");
  const [service, setService] = useState<TesterService>("mass_notification");
  const [channels, setChannels] = useState<Set<TesterChannel>>(
    () => new Set<TesterChannel>(["voice"]),
  );
  const [callerId, setCallerId] = useState(DEFAULT_DIALOUT_CALLER_ID);
  const [targetPhone, setTargetPhone] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  // C1 "Maximum Ports" — must be > 0 for a voice blast. One number = one port.
  const [maxPorts, setMaxPorts] = useState("1");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<TesterRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedTarget = targets.find((t) => t.id === targetId);

  // When a target is chosen, default the caller ID to its configured DID.
  const onTargetChange = (id: number | "") => {
    setTargetId(id);
    const t = targets.find((x) => x.id === id);
    if (t) setCallerId(t.dialout_caller_id || DEFAULT_DIALOUT_CALLER_ID);
  };

  const toggleChannel = (ch: TesterChannel) =>
    setChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });

  const phoneValid = /^\+?[1-9]\d{5,14}$/.test(targetPhone.trim());
  const wantsEmail = channels.has("email");
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(targetEmail.trim());
  const ready =
    canRun &&
    typeof targetId === "number" &&
    channels.size > 0 &&
    phoneValid &&
    (!wantsEmail || emailValid) &&
    TESTER_LIVE_SERVICES.includes(service);

  const execute = async () => {
    if (typeof targetId !== "number") return;
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const res = await testerApi.run({
        target_id: targetId,
        service: "mass_notification",
        channels: [...channels],
        caller_id: callerId.trim() || DEFAULT_DIALOUT_CALLER_ID,
        target_phone: targetPhone.trim(),
        max_channels: Number(maxPorts) > 0 ? Math.floor(Number(maxPorts)) : 1,
        ...(targetEmail.trim() ? { target_email: targetEmail.trim() } : {}),
        ...(subject.trim() ? { subject: subject.trim() } : {}),
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      setResult(res);
    } catch (err) {
      // The server still returns a structured result body on a 502; api()
      // throws an ApiError whose details carry it. Fall back to the message.
      setError(err instanceof ApiError ? err.message : "The test failed to run.");
    } finally {
      setRunning(false);
    }
  };

  if (targetsQuery.isSuccess && targets.length === 0) {
    return (
      <Box>
        <PageHeader title="Run a test" />
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <Box aria-hidden sx={{ width: 200 }}>
            <SignalEmitter />
          </Box>
        </Box>
        <EmptyState
          title="No test servers yet"
          description="Register a server and its API keys before you can run a capability test."
          action={
            <Button component={RouterLink} to="../servers" variant="contained">
              Configure a server
            </Button>
          }
        />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Run a capability test"
        description="Pick a server and a value-added service, choose the channels, and fire a real test against a phone number you control."
      />

      <Card variant="outlined" sx={{ p: 3, maxWidth: 720 }}>
        <Stack spacing={3}>
          {/* Target + service */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="tester-target-label">Server</InputLabel>
              <Select
                labelId="tester-target-label"
                label="Server"
                value={targetId}
                onChange={(e) =>
                  onTargetChange(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
              >
                {targets.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="tester-service-label">Service</InputLabel>
              <Select
                labelId="tester-service-label"
                label="Service"
                value={service}
                onChange={(e) => setService(e.target.value as TesterService)}
              >
                {TESTER_SERVICES.map((s) => {
                  const live = TESTER_LIVE_SERVICES.includes(s);
                  return (
                    <MenuItem key={s} value={s} disabled={!live}>
                      {TESTER_SERVICE_LABELS[s]}
                      {!live && (
                        <Chip
                          size="small"
                          label="Soon"
                          sx={{ ml: 1 }}
                          variant="outlined"
                        />
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Stack>

          <Divider />

          {/* Channels */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Channels to test
            </Typography>
            <FormGroup row>
              {TESTER_CHANNELS.map((ch) => (
                <FormControlLabel
                  key={ch}
                  control={
                    <Checkbox
                      checked={channels.has(ch)}
                      onChange={() => toggleChannel(ch)}
                    />
                  }
                  label={TESTER_CHANNEL_LABELS[ch]}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Destination + caller id */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Destination phone"
              value={targetPhone}
              onChange={(e) => setTargetPhone(e.target.value)}
              fullWidth
              required
              placeholder="+37126030544"
              error={targetPhone.length > 0 && !phoneValid}
              helperText={
                targetPhone.length > 0 && !phoneValid
                  ? "Enter a phone number in E.164 form."
                  : "Added as an ad-hoc recipient on the notification."
              }
            />
            <TextField
              label="Dial-out caller ID"
              value={callerId}
              onChange={(e) => setCallerId(e.target.value)}
              fullWidth
              helperText="Defaults to the server’s configured DID (+18287608073)."
            />
            <TextField
              label="Max ports"
              value={maxPorts}
              onChange={(e) => setMaxPorts(e.target.value.replace(/[^0-9]/g, ""))}
              type="number"
              inputProps={{ min: 1, max: 100 }}
              sx={{ width: { xs: "100%", sm: 160 }, flexShrink: 0 }}
              helperText="Voice dial-out ports. 1 is enough for one number."
            />
          </Stack>

          {/* Email recipient — required for the email channel to reach anyone. */}
          <TextField
            label={`Destination email${wantsEmail ? " *" : " (for the Email channel)"}`}
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            fullWidth
            type="email"
            placeholder="you@example.com"
            error={wantsEmail && targetEmail.length > 0 && !emailValid}
            helperText={
              wantsEmail
                ? "The email is sent to this address (the ad-hoc recipient's email)."
                : "Only needed if you tick the Email channel."
            }
          />

          {channels.has("whatsapp") && (
            <Alert severity="info" variant="outlined">
              WhatsApp only sends if the server’s Twilio is WhatsApp-armed and
              the recipient has opted in (messaged the sandbox/number within the
              24-hour window or accepted a template). That’s configured on C1,
              not here — if it doesn’t arrive, the message was accepted but
              WhatsApp wasn’t armed for this number.
            </Alert>
          )}

          {/* Message */}
          <TextField
            label="Subject (optional)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
          />
          <TextField
            label="Message text (SMS / WhatsApp / email / push — and spoken aloud for voice)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="This is an automated XOP capability test."
            helperText="For a voice call, this text is turned into speech (TTS) and played to the recipient."
          />

          {!canRun && (
            <Alert severity="info" variant="outlined">
              You can browse this surface but need the <strong>tester:run</strong>{" "}
              permission to fire a live test.
            </Alert>
          )}

          <Box>
            <Button
              variant="contained"
              size="large"
              disabled={!ready || running}
              onClick={() => setConfirmOpen(true)}
            >
              {running ? "Running…" : "Run test"}
            </Button>
          </Box>
        </Stack>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mt: 3, maxWidth: 720 }}>
          {error}
        </Alert>
      )}

      {result && <RunResultCard result={result} />}

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Fire a live test?"
        description={`This sends REAL traffic — ${[...channels]
          .map((c) => TESTER_CHANNEL_LABELS[c])
          .join(", ")} to ${targetPhone || "the destination"} via ${
          selectedTarget?.name ?? "the server"
        }.`}
        variant="warning"
        confirmLabel="Send it"
        onConfirm={async () => {
          setConfirmOpen(false);
          await execute();
        }}
      />
    </Box>
  );
}

function RunResultCard({ result }: { result: TesterRunResult }): JSX.Element {
  return (
    <Alert
      severity={result.ok ? "success" : "error"}
      sx={{ mt: 3, maxWidth: 720 }}
      data-testid="tester-run-result"
    >
      <AlertTitle>
        {result.ok ? "Test sent" : "Test failed"}
        {result.notification_id ? ` · notification #${result.notification_id}` : ""}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {result.message}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mb: 1 }}>
        {result.steps.map((s) => (
          <Chip
            key={s.name}
            size="small"
            label={`${s.name}: ${s.ok ? "ok" : "failed"}`}
            color={s.ok ? "success" : "error"}
            variant="outlined"
          />
        ))}
      </Stack>
      {result.errors.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {result.errors.map((e, i) => (
            <li key={i}>
              <Typography variant="body2">{e}</Typography>
            </li>
          ))}
        </Box>
      )}
    </Alert>
  );
}
